/**
 * dosageDisplay.js — Shared utilities for rendering supplement dosage info.
 *
 * Used by: SupplementsTable.js, StackCard.js
 *
 * Design constraints:
 *  - dosageDetails fields come from Gemini PDF extraction via get_best_dosage()
 *    in backend/endpoints/research_stack_pdf.py and can be highly variable.
 *  - All helpers are null-safe: unknown/variable/noisy values are suppressed rather
 *    than shown in a confusing/truncated form.
 *  - Saved stacks in Firestore may contain legacy shapes (pre-normalisation).
 *    Frontend formatters must handle both normalised and raw values.
 */

// ---------------------------------------------------------------------------
// Contract: types produced by the backend and persisted in Firestore
// ---------------------------------------------------------------------------

/**
 * Dosage source — set by get_best_dosage() / stack assembly in research_stack_pdf.py.
 *
 * @typedef {'pdf_verified' | 'pdf_verified_overridden' | 'study' | 'recommendation' | 'initial_empty'} DosageSource
 *
 *  pdf_verified  — dosage derived from full-text PDF extraction (highest confidence)
 *  pdf_verified_overridden — was pdf_verified, then the AI safety review replaced
 *                  the amount (research_stack_pdf.py:1833). Verified 2026-08-14;
 *                  it was missing from this list, so treat the list as the
 *                  contract and check the backend before assuming it is closed.
 *  study         — dosage extracted by LLM from abstract text
 *  recommendation — AI-adjusted dosage (e.g. capped for safety)
 *  initial_empty — no dosage data available at generation time
 *
 * Only the first and third trace to a trial. The two AI-adjusted sources carry
 * a number no study printed, which is why isClinicalStudyDose excludes them.
 */

/**
 * Structured dosing details attached to a supplement when dosageSource is 'pdf_verified'.
 * Built by get_best_dosage() from dosing_protocol.interventions[0].
 *
 * Backend normalises duration_weeks to numeric (int/float) since 2026-02.
 * Legacy Firestore stacks may still contain string values (e.g. "8 weeks").
 *
 * @typedef {Object} DosageDetails
 * @property {string|null}  frequency       - e.g. "once daily", "twice daily". 150+ unique
 *                                            values in production; many are noisy for meta-analyses.
 * @property {string|null}  per_dose_amount - Per-intake amount as string, e.g. "500".
 * @property {string|null}  form            - e.g. "capsule", "tablet". 130+ unique values.
 * @property {string|null}  timing          - e.g. "with meals", "on empty stomach".
 * @property {number|string|null} duration_weeks - Study duration. Numeric after normalisation;
 *                                                 may be string in legacy stacks.
 * @property {string|null}  route           - e.g. "oral", "sublingual", "IV".
 */

/**
 * Supplement object shape (dosage-relevant fields only).
 *
 * @typedef {Object} SupplementDosageFields
 * @property {string|number|null} dosage        - Total daily amount (string from backend, e.g. "1200").
 * @property {string|null}        unit          - Canonical unit, e.g. "mg", "mcg", "IU".
 * @property {DosageSource}       dosageSource  - Origin of the dosage value.
 * @property {DosageDetails|null} dosageDetails - Rich protocol info; present when dosageSource is 'pdf_verified'.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';

// ---------------------------------------------------------------------------
// Frequency
// ---------------------------------------------------------------------------

// Values that signal "we don't have a usable frequency" — suppress display.
const FREQUENCY_SUPPRESS = new Set([
  'not specified', 'not stated', 'not explicitly stated', 'unspecified',
  'null', 'n/a', 'na', 'unknown', 'variable', 'varies', 'various',
  'varied', 'varied by study', 'varies by study', 'mixed',
  'not standardized across meta-analysis', 'not specified across all studies',
  'varied across studies', 'varied across trials', 'varies across trials',
]);

const DETAIL_VALUE_NA = new Set([
  'not specified', 'not stated', 'not explicitly stated', 'unspecified',
  'null', 'n/a', 'na', 'unknown',
]);

function formatDetailValue(value) {
  if (value === null || value === undefined) return 'N/A';
  if (Array.isArray(value)) {
    const joined = value.map((item) => String(item).trim()).filter(Boolean).join(', ');
    return joined || 'N/A';
  }

  const text = String(value).trim();
  if (!text || DETAIL_VALUE_NA.has(text.toLowerCase())) return 'N/A';
  return text;
}

// Patterns → compact label.  Order matters: check most specific first.
// The generic /\bdaily\b/ catch-all MUST come after all specific multi-dose
// patterns, otherwise "twice daily" matches /\bdaily\b/ first → "once/day".
// Each "N times" pattern accepts "daily | a day | per day | /day" so that
// "twice a day" and "twice daily" are both captured.
const _DAY = '(daily|a\\s*day|per\\s*day|\\/day)';
const FREQUENCY_PATTERNS = [
  [new RegExp(`\\b(once|1\\s*x|1\\s*time)\\s*(?:a\\s*)?${_DAY}\\b`, 'i'), 'once/day'],
  [new RegExp(`\\btwice\\s*(?:a\\s*)?${_DAY}\\b`, 'i'),                    '2×/day'],
  [new RegExp(`\\b2\\s*(x|times)\\s*(?:a\\s*)?${_DAY}\\b`, 'i'),           '2×/day'],
  [/\b(b\.?i\.?d\.?|bid)\b/i,                                               '2×/day'],
  [new RegExp(`\\bthree\\s*times\\s*(?:a\\s*)?${_DAY}\\b`, 'i'),                          '3×/day'],
  [new RegExp(`\\b(t\\.?i\\.?d\\.?|tid|thrice\\s*(?:a\\s*)?${_DAY})\\b`, 'i'),           '3×/day'],
  [new RegExp(`\\b3\\s*(x|times)\\s*(?:a\\s*)?${_DAY}\\b`, 'i'),            '3×/day'],
  [new RegExp(`\\bfour\\s*times\\s*(?:a\\s*)?${_DAY}\\b`, 'i'),             '4×/day'],
  [/\b(q\.?i\.?d\.?|qid)\b/i,                                               '4×/day'],
  [new RegExp(`\\b4\\s*(x|times)\\s*(?:a\\s*)?${_DAY}\\b`, 'i'),            '4×/day'],
  [/\bdaily\b/i,                                                            'once/day'],
  [/\bonce\s*(a\s*)?week(ly)?\b/i,                                  'weekly'],
  [/\bweekly\b/i,                                                   'weekly'],
  [/\btwice\s*(a\s*)?week(ly)?\b/i,                                '2×/week'],
  [/\bthree\s*times\s*(a\s*)?week(ly)?\b/i,                        '3×/week'],
  [/\bbiweekly\b/i,                                                'biweekly'],
  [/\bonce\s*(a\s*)?month(ly)?\b/i,                                'monthly'],
  [/\bmonthly\b/i,                                                 'monthly'],
  [/\bsingle\s*dose\b/i,                                         'single dose'],
];

/**
 * Returns true when the compact frequency label implies the total daily dose
 * is split across multiple doses (e.g. "2×/day", "3×/day", "4×/day").
 * Callers should display per_dose_amount instead of the total daily dose when
 * this returns true and dosageDetails.per_dose_amount is available.
 */
export function isDividedDosing(freqLabel) {
  return typeof freqLabel === 'string' && /^[2-9]×\/day$/.test(freqLabel);
}

/**
 * Returns the dose amount that should be rendered in stack UI.
 *
 * For PDF-verified protocols, dosageDetails.per_dose_amount is the source of
 * truth for the displayed protocol dose. The top-level dosage can represent a
 * derived total daily amount, which is useful internally but can diverge from
 * the PDF protocol display (for example, "10 mg once daily" incorrectly shown
 * as "20 mg"). Fall back to top-level dosage for legacy stacks that lack
 * dosageDetails.
 */
export function getDisplayDosage(supplement) {
  if (!supplement) return null;

  const perDose = supplement.dosageDetails?.per_dose_amount;
  if (
    supplement.dosageSource === 'pdf_verified' &&
    perDose !== null &&
    perDose !== undefined &&
    String(perDose).trim() !== ''
  ) {
    return perDose;
  }

  return supplement.dosage;
}

/**
 * Rounds a dose amount for display to at most one decimal place.
 *
 * The backend derives per_dose_amount by division, so a 14 g/day protocol at
 * 3×/day arrives as `4.6667` and rendered "4.6667 g" (Psyllium Husk, reported
 * 2026-08-14). One decimal is past any dose precision a user acts on.
 *
 * Returns a STRING, and returns non-numeric input untouched — some doses are
 * ranges or free text, and coercing those would destroy them.
 */
export function formatDoseAmount(value) {
  if (value === null || value === undefined) return value;
  const text = String(value).trim();
  if (text === '') return value;

  const numeric = Number(text);
  if (!Number.isFinite(numeric)) return value;

  return String(Number(numeric.toFixed(1)));
}

/**
 * True when the dose on screen is the one the system generated from research,
 * and the user has not since replaced it.
 *
 * Gated on the two sources that actually trace to a study. A `recommendation`
 * dose is system-generated too, but it is AI-adjusted rather than read off a
 * trial, so labelling it "clinical study dose" would claim a provenance it does
 * not have — the same error as claiming a prescription check that never ran.
 *
 * `userEdited` is set by applyEditedDose. Once a user touches the dose the
 * marker stays off even if they type the original number back: the chip has
 * been user-owned since (ruled by Tanu, 2026-08-14).
 */
export function isClinicalStudyDose(supplement) {
  if (!supplement || supplement.userEdited) return false;
  return supplement.dosageSource === 'pdf_verified' || supplement.dosageSource === 'study';
}

/**
 * Apply an inline dose-chip edit onto a supplement.
 *
 * The chip displays getDisplayDosage(); the editor seeds from that same value.
 * When the display came from dosageDetails.per_dose_amount, the commit must
 * update that field (and keep top-level `dosage` as the daily total when
 * frequency is N×/day). Writing only `dosage` leaves the chip stuck on the
 * old per-dose number after confirm and after save/reload.
 *
 * @param {SupplementDosageFields} supplement
 * @param {string} dosage — the amount the user typed (display units)
 * @param {string} unit
 * @returns {SupplementDosageFields}
 */
export function applyEditedDose(supplement, dosage, unit) {
  const prev = supplement || {};
  // Marks the dose as user-owned from here on, which is what hides the
  // clinical-study marker. Set before the branch so both paths carry it.
  const next = { ...prev, unit, userEdited: true };
  const details = prev.dosageDetails;
  const shownViaPerDose =
    prev.dosageSource === 'pdf_verified' &&
    details &&
    details.per_dose_amount != null &&
    String(details.per_dose_amount).trim() !== '';

  if (shownViaPerDose) {
    const freqLabel = formatFrequencyCompact(details.frequency);
    const timesMatch =
      typeof freqLabel === 'string' ? freqLabel.match(/^([2-9])×\/day$/) : null;
    const timesPerDay = timesMatch ? Number(timesMatch[1]) : 1;
    const numeric = Number(dosage);
    next.dosageDetails = {
      ...details,
      per_dose_amount: dosage,
    };
    // `toFixed(4)` before `Number` drops binary-float noise — 0.1 × 3 is
    // 0.30000000000000004 in IEEE 754, and that string is what would be saved
    // and shown as the daily total. Four places is past any real dose
    // precision, so a genuine decimal survives untouched.
    next.dosage =
      dosage === '' || Number.isNaN(numeric)
        ? dosage
        : String(Number((numeric * timesPerDay).toFixed(4)));
    return next;
  }

  next.dosage = dosage;
  return next;
}

/**
 * Returns a compact, user-facing frequency label (e.g. "2×/day") or null.
 * Returns null for noisy/variable/unrecognisable values so the caller can
 * omit the display element entirely.
 */
export function formatFrequencyCompact(frequency) {
  if (!frequency || typeof frequency !== 'string') return null;
  const normalised = frequency.trim().toLowerCase();
  if (FREQUENCY_SUPPRESS.has(normalised)) return null;

  for (const [pattern, label] of FREQUENCY_PATTERNS) {
    if (pattern.test(frequency)) return label;
  }

  // Suppress anything that looks variable or too complex to summarise.
  if (
    normalised.includes('varies') ||
    normalised.includes('varied') ||
    normalised.includes('variable') ||
    normalised.includes('not specified') ||
    normalised.includes('or ') || // "once or twice daily" → too ambiguous
    normalised.includes('|') ||
    normalised.includes(';') ||
    normalised.length > 20
  ) {
    return null;
  }

  // Short, clean string that didn't match — show as-is (capitalised).
  return frequency.charAt(0).toUpperCase() + frequency.slice(1);
}

// ---------------------------------------------------------------------------
// Duration
// ---------------------------------------------------------------------------

/**
 * Normalises duration_weeks (int | float | str) to a display string.
 * Strips "week/weeks" suffix if already present in string values.
 * Returns null for non-numeric or nonsensical values.
 */
export function formatDuration(duration_weeks) {
  if (duration_weeks === null || duration_weeks === undefined) return null;

  let num;
  if (typeof duration_weeks === 'number') {
    num = duration_weeks;
  } else if (typeof duration_weeks === 'string') {
    // Strip "week(s)" and whitespace, then try to parse
    const cleaned = duration_weeks.replace(/\bweeks?\b/gi, '').trim();
    num = parseFloat(cleaned);
  } else {
    return null;
  }

  if (!isFinite(num) || num <= 0) return null;

  // Convert fractional weeks to days for sub-week durations
  if (num < 1) {
    const days = Math.round(num * 7);
    return days === 1 ? '1 day' : `${days} days`;
  }

  const rounded = Number.isInteger(num) ? num : Math.round(num * 10) / 10;
  return rounded === 1 ? '1 week' : `${rounded} weeks`;
}

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

/**
 * Returns the first/primary form descriptor, capitalised, truncated.
 * Handles compound values like "capsule, tablet, or powder" → "Capsule".
 */
export function formatForm(form) {
  if (!form || typeof form !== 'string') return null;
  const normalised = form.toLowerCase().trim();
  if (['not specified', 'n/a', 'na', 'unknown', 'null'].includes(normalised)) return null;

  // Take just the first option when multiple are listed
  const first = form.split(/[,/|]/)[0].trim();
  if (!first) return null;
  const label = first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  // Truncate if still too long
  return label.length > 24 ? label.slice(0, 22) + '…' : label;
}

// ---------------------------------------------------------------------------
// Tooltip builder (React element)
// ---------------------------------------------------------------------------

/**
 * Builds a rich MUI Tooltip title for a supplement's dosage info icon.
 *   - pdf_verified: full structured protocol details
 *   - study:        simple one-liner
 *   - other:        null (caller should not show icon)
 *
 * @param {SupplementDosageFields} supplement
 * @returns {React.ReactElement|string|null}
 */
export function buildDosageTooltip(supplement) {
  if (supplement.dosageSource === 'pdf_verified' && supplement.dosageDetails) {
    const d = supplement.dosageDetails;
    const hasDetail = (field) => Object.prototype.hasOwnProperty.call(d, field);
    const duration = formatDuration(d.duration_weeks);
    const form = formatForm(d.form);
    const freq = formatDetailValue(d.frequency);
    const perDose = formatDetailValue(d.per_dose_amount);
    const timing = formatDetailValue(d.timing);
    const unit = formatDetailValue(supplement.unit);
    const formLabel = form || formatDetailValue(d.form);
    const durationLabel = duration || formatDetailValue(d.duration_weeks);
    const perDoseText = perDose === 'N/A'
      ? 'N/A'
      : `${perDose}${unit !== 'N/A' ? ` ${unit}` : ''}`;

    return (
      <Box sx={{ p: 0.25 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
          PDF-verified dosing
        </Typography>
        {hasDetail('frequency') && (
          <Typography variant="caption" display="block">
            Frequency: {freq}
          </Typography>
        )}
        {hasDetail('per_dose_amount') && (
          <Typography variant="caption" display="block">
            Per dose: {perDoseText}
          </Typography>
        )}
        {hasDetail('form') && (
          <Typography variant="caption" display="block">
            Form: {formLabel}
          </Typography>
        )}
        {hasDetail('timing') && (
          <Typography variant="caption" display="block">
            Timing: {timing}
          </Typography>
        )}
        {hasDetail('duration_weeks') && (
          <Typography variant="caption" display="block">
            Study duration: {durationLabel}
          </Typography>
        )}
      </Box>
    );
  }

  if (supplement.dosageSource === 'study') {
    const displayDosage = getDisplayDosage(supplement);
    const unit = formatDetailValue(supplement.unit);
    if (displayDosage !== null && displayDosage !== undefined && String(displayDosage).trim()) {
      return `Clinical study dosage: ${displayDosage}${unit !== 'N/A' ? ` ${unit}` : ''}`;
    }
    return 'Clinical study dosage: N/A';
  }

  return null;
}
